import { type Filter } from '@spendesk/bookkeeping-core-types';
import { useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { type PayableSubType } from 'modules/bookkeep/payables/containers/PayablePanelContainer';
import { type FiltersState } from 'modules/bookkeep/payables/containers/PayablesFiltersContainer/hooks/useFiltersContext';
import type { Payable } from 'modules/payable/models';
import { useInfiniteQuery } from 'src/core/api/hooks/useInfiniteQuery';
import { type InfiniteQueryState } from 'src/core/api/queryState';
import {
  type FilterWithOptionalField,
  withoutOptionalFields,
} from 'src/core/common/components/FilterBuilder';

import { GET_PAYABLE_LIST, type RawResult } from './query';
import {
  reshapeMember,
  reshapeSupplier,
  reshapeCounterparty,
  reshapeMileageDetails,
  reshapePerDiem,
} from './reshaper';
import { toCoreTypeFilter } from '../payable-filter/payableFilterTransformer';
import { getPayableListQueryKey } from '../query-key-registry';

export const useInvalidateGetPayableListQueryCache = () => {
  const queryClient = useQueryClient();

  return async (companyId: string): Promise<void> => {
    await queryClient.invalidateQueries(getPayableListQueryKey(companyId));
  };
};

export type PayableListRawResponse = RawResult;

export type PayableListVariables = {
  companyId: string;
  cursor: string | number | undefined;
  filtersV2: Filter | undefined;
  textualSearch: string | undefined;
};

export const useGetPayableList = ({
  filters,
  filtersV2: rawFiltersV2,
  options: { isEnabled = true, ...options },
}: {
  filters: FiltersState;
  filtersV2: FilterWithOptionalField | undefined;
  options: { onSuccess: () => void; isEnabled?: boolean };
}): InfiniteQueryState<Payable[]> => {
  const companyId = useCompanyId();

  const filtersV2 = rawFiltersV2
    ? toCoreTypeFilter(withoutOptionalFields(rawFiltersV2))
    : undefined;

  return useInfiniteQuery<Payable, RawResult>({
    key: getPayableListQueryKey(companyId, filters, filtersV2),
    getRequest: (cursor) => {
      const variables: PayableListVariables = {
        companyId,
        cursor,
        filtersV2,
        textualSearch: filters.search,
      };

      return {
        type: 'graphQL',
        target: 'v2',
        query: GET_PAYABLE_LIST,
        variables,
      };
    },
    getNextPageParam(data) {
      if (data.company?.payables?.pageInfo?.hasNextPage) {
        const { edges, pageInfo } = data.company.payables;
        const lastEdge = edges.at(-1);

        if (pageInfo.hasNextPage && lastEdge) {
          return lastEdge.cursor;
        }
        return undefined;
      }
    },
    reshapeData(data) {
      return data.company.payables.edges.map(({ node: rawPayable }) => {
        return {
          id: rawPayable.id,
          version: rawPayable.version,
          subtype: (rawPayable.subtype as PayableSubType) || undefined,
          type: rawPayable.type,
          kind: rawPayable.kind,
          description: rawPayable.description,
          counterparty: rawPayable.counterparty
            ? reshapeCounterparty(rawPayable.counterparty)
            : undefined,
          member: rawPayable.member
            ? reshapeMember(rawPayable.member)
            : undefined,
          supplier: rawPayable.supplier
            ? reshapeSupplier(rawPayable.supplier)
            : undefined,
          documentaryEvidence: rawPayable.documentaryEvidence,
          grossAmount: rawPayable.grossAmount,
          functionalAmount: rawPayable.functionalAmount,
          creationDate: new Date(rawPayable.creationDate),
          mileageDetails: rawPayable.mileageDetails
            ? reshapeMileageDetails(rawPayable.mileageDetails)
            : undefined,
          perDiem: rawPayable.perDiem
            ? reshapePerDiem(rawPayable.perDiem)
            : undefined,
          costCenter: rawPayable.costCenter,
        };
      });
    },
    options,
    isEnabled,
  });
};
